class UpsellVariantSelector extends HTMLElement {
  constructor() {
    super();

    this.modalElement = this.closest("x-drawer");
    this.variants = this.getProductVariants();
    this.referenceCheckboxId = this.getAttribute("reference-checkbox-id");
    this.optionNames = this.getAttribute("option-names");
    this.selectedVariant = null;
    this.moneyFormat = this.getAttribute("data-money-format");
    this.shopLocale = this.getAttribute("data-shop-locale");
    // The Black Weeks percentage the checkout takes off this cover (Liquid, kc-bf-cover-off); 0 = list price.
    this.coverOff = parseInt(this.dataset.kcCoverOff || "0", 10) || 0;
    this.sofaValues = this.readSofa();
    this.locks = this.computeLocks();

    this.form = this.querySelector("form");
    this.applyLockUi();
    if (this.form) {
      this.form.addEventListener("change", this.handleFormChange.bind(this));
    }

    if (this.modalElement) {
      this.saveButton = this.modalElement.querySelector(
        'button[aria-controls="upsell-modal-save-btn"]'
      );
      if (this.saveButton) {
        this.saveButton.addEventListener(
          "click",
          this.handleSaveButtonClick.bind(this)
        );
      }
    }

    this.preselectCurrentVariant();
  }

  getProductVariants() {
    try {
      const scriptElement = this.querySelector(
        'script[type="application/json"]'
      );
      if (!scriptElement) {
        return [];
      }
      const productVariants = scriptElement.textContent;
      return JSON.parse(productVariants);
    } catch (error) {
      return [];
    }
  }

  preselectCurrentVariant() {
    const referenceCheckbox = document.querySelector(
      `input[data-upsell-product-key="${this.referenceCheckboxId}"]`
    );

    let currentVariant = null;
    // What goes into the cart is the slot input; upsell.js re-matches the checkbox's value to the sofa's fabric
    // on its own, so the slot wins when it holds one of this product's variants (not the voucher's, say).
    const slotInput = document.querySelector(`input[data-input-id="${this.referenceCheckboxId}"]`);
    if (slotInput) {
      currentVariant = this.variants.find((v) => v.id === parseInt(slotInput.value, 10)) || null;
    }
    if (!currentVariant && referenceCheckbox) {
      const currentVariantId = parseInt(referenceCheckbox.value, 10);
      currentVariant = this.variants.find((v) => v.id === currentVariantId);
    }
    if (!currentVariant) {
      currentVariant =
        this.variants.find((variant) => variant.available) || this.variants[0];
    }
    if (!currentVariant) return;
    currentVariant = this.lockedTwin(currentVariant);
    this.selectedVariant = currentVariant;

    this.preselectOptionsSequentially(currentVariant);
  }

  async preselectOptionsSequentially(variant) {
    if (!this.form) return;

    for (let i = 1; i <= 3; i++) {
      const optionValue = variant[`option${i}`];
      if (!optionValue || optionValue === "Default Title") continue;

      const groupRadios = this.form.querySelectorAll(
        'input[name^="variant-group-"]'
      );
      if (groupRadios.length > 0 && i === 1) {
        let matchedGroupRadio = null;
        for (const groupRadio of groupRadios) {
          if (optionValue.includes(groupRadio.value)) {
            matchedGroupRadio = groupRadio;
            break;
          }
        }

        if (matchedGroupRadio) {
          matchedGroupRadio.checked = true;
          matchedGroupRadio.dispatchEvent(
            new Event("change", { bubbles: true })
          );

          // Wait for animations to complete
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
      }

      const radioInput = this.form.querySelector(
        `input[type="radio"][value="${optionValue}"]`
      );

      if (radioInput) {
        radioInput.checked = true;
        radioInput.dispatchEvent(new Event("change", { bubbles: true }));

        const optionName = radioInput.name;
        const variantTitleValue = this.querySelector(
          `.variant-list-title-value[data-option-name="${optionName}"]`
        );
        if (variantTitleValue) {
          variantTitleValue.textContent = optionValue;
        }

        await new Promise((resolve) => requestAnimationFrame(resolve));
        continue;
      }

      const selectElements = this.form.querySelectorAll("select");

      for (const selectElement of selectElements) {
        const option = Array.from(selectElement.options).find(
          (opt) => opt.value === optionValue
        );
        if (option) {
          selectElement.value = optionValue;
          selectElement.dispatchEvent(new Event("change", { bubbles: true }));

          const optionName = selectElement.name;
          const variantTitleValue = this.querySelector(
            `.variant-list-title-value[data-option-name="${optionName}"]`
          );
          if (variantTitleValue) {
            variantTitleValue.textContent = optionValue;
          }

          await new Promise((resolve) => requestAnimationFrame(resolve));
          break;
        }
      }
    }

    this.updateContent(this.selectedVariant);
  }

  handleFormChange(event) {
    event.preventDefault();
    event.stopPropagation();

    const formData = new FormData(this.form);
    const filteredFormData = Array.from(formData).filter(
      ([key]) => this.optionNames.indexOf(key) >= 0
    );
    const formDataObject = Object.fromEntries(filteredFormData);

    const result = this.variants.find((variant) => {
      return Object.values(formDataObject).every(
        (value, index) => variant[`option${index + 1}`] === value
      );
    });

    filteredFormData.forEach(([key, value]) => {
      const variantTitle = this.querySelector(
        `.variant-list-title-value[data-option-name="${key}"]`
      );
      if (variantTitle) {
        variantTitle.textContent = value;
      }
    });

    this.selectedVariant = result;
    this.updateContent(result);

    if (this.selectedVariant && !this.saveButton) {
      document.querySelectorAll(`input[data-input-id="${this.referenceCheckboxId}"]`)
        .forEach((input) => { input.value = this.selectedVariant.id; });
      this.notifyVariantSelected(this.selectedVariant);
    }
  }

  updateContent(variant) {
    if (!variant) return;

    this.updateCtaLabel(variant);

    const productItem = this.closest(".upsell-product-item");
    const searchContext = productItem || this.modalElement;

    if (!searchContext) {
      return;
    }

    const imageSrc =
      (variant.featured_image && variant.featured_image.src) ||
      (variant.featured_media &&
        variant.featured_media.preview_image &&
        variant.featured_media.preview_image.src) ||
      null;

    const upsellMedia = searchContext.querySelector(".image-block .image-wrapper");
    if (upsellMedia && imageSrc) {
      const media = `<img src="${imageSrc}" alt="${variant.title}">`;
      this.replaceMedia(upsellMedia, media, imageSrc);
    }

    const priceList = searchContext.querySelector(".price-list");
    if (priceList) {
      this.replacePriceList(priceList, variant);
    }

    if (productItem) {
      const variantIdInput = productItem.querySelector(
        'input[data-variant-id-input][name="id"]'
      );
      if (variantIdInput) {
        variantIdInput.value = variant.id;
      }
    }
  }

  /** The modal's CTA names the colour it will select: "<button text> Pumpkin Velour". */
  updateCtaLabel(variant) {
    const button = this.saveButton;
    if (!button) return;
    const label =
      button.querySelector("[data-cta-label]") ||
      [...button.querySelectorAll("span, div")].find((el) => !el.children.length && el.textContent.trim());
    if (!label) return;
    if (!label.hasAttribute("data-cta-label")) {
      label.setAttribute("data-cta-label", "");
      label.dataset.baseLabel = label.textContent.trim();
    }
    const colour = String(variant.option1 || variant.title || "").replace(/-/g, " ").trim();
    label.textContent = colour ? `${label.dataset.baseLabel} ${colour}` : label.dataset.baseLabel;
  }

  /**
   * Broadcast the newly selected upsell variant so other components (e.g. the
   * sticky add-to-cart bar) can react. Writes the variant id + price onto every
   * matching upsell item container so the price is readable from the DOM, then
   * dispatches `upsell:variant-changed`. Prices are in cents.
   */
  notifyVariantSelected(variant) {
    if (!variant) return;

    // The human-readable side of the pick, for listeners that render a
    // summary line (kc-bf-offer.js) and don't hold the variant list.
    const variantOptions = [variant.option1, variant.option2, variant.option3]
      .filter((opt) => opt && opt !== "Default Title")
      .join(" / ");
    const imageSrc =
      (variant.featured_image && variant.featured_image.src) ||
      (variant.featured_media &&
        variant.featured_media.preview_image &&
        variant.featured_media.preview_image.src) ||
      null;

    document
      .querySelectorAll(
        `input[data-upsell-product-key="${this.referenceCheckboxId}"]`
      )
      .forEach((checkbox) => {
        const container = checkbox.closest("[data-upsell-matcher]");
        if (!container) return;
        container.dataset.currentVariantId = variant.id;
        container.dataset.currentVariantPrice = variant.price;
        container.dataset.currentVariantCompareAtPrice =
          variant.compare_at_price || "";
      });

    document.dispatchEvent(
      new CustomEvent("upsell:variant-changed", {
        bubbles: true,
        detail: {
          referenceCheckboxId: this.referenceCheckboxId,
          variantId: variant.id,
          price: variant.price,
          compareAtPrice: variant.compare_at_price || null,
          variantTitle: variant.title || "",
          variantOptions,
          imageSrc,
        },
      })
    );
  }

  /**
   * /cart "Bearbeiten": the modal is opened for a line already in the cart, so the picker must start on THAT line's
   * variant (there is no reference checkbox to read it from). Called by <main-cart> (assets/cart.js) before show().
   */
  selectVariantById(variantId) {
    let variant = this.variants.find((v) => v.id === Number(variantId));
    if (!variant) return;
    variant = this.lockedTwin(variant);
    this.selectedVariant = variant;
    this.preselectOptionsSequentially(variant);
  }

  // ── The cover follows the sofa (Ivan 04/11) ───────────────────────────────────────────────────────────────
  // A cover's further options (Terra: Sitztiefe, Konfiguration; Mike: Variante) are not the customer's to choose:
  // they are the sofa's. data-kc-sofa holds the sofa variant's option values (_kc-upsell-modal-variant-picker:
  // the product page's selected variant, or on /cart the sofa line this cover belongs to). Every further option
  // of this product whose values include one of the sofa's is LOCKED to it: shown read-only, and every variant
  // this selector picks is the one with the same fabric and the locked values. Matched by value, not by option
  // name, so a translated option name ("Seat depth") does not lose it.

  readSofa() {
    try {
      const values = JSON.parse(this.dataset.kcSofa || "null");
      return Array.isArray(values) ? values.filter((value) => value != null && value !== "") : null;
    } catch (error) {
      return null;
    }
  }

  /** { optionIndex (2|3): sofa's value } for each further option of this product that the sofa decides */
  computeLocks() {
    const locks = {};
    if (!this.sofaValues || this.sofaValues.length < 2) return locks;
    for (let i = 2; i <= 3; i++) {
      const values = new Set(this.variants.map((v) => v[`option${i}`]).filter(Boolean));
      if (!values.size) continue;
      const value = this.sofaValues.slice(1).find((sofaValue) => values.has(sofaValue));
      if (value !== undefined) locks[i] = value;
    }
    return locks;
  }

  hasLocks() {
    return Object.keys(this.locks || {}).length > 0;
  }

  /** the variant in the same fabric/colour with the sofa's values in the locked options (available first) */
  lockedTwin(variant) {
    if (!variant || !this.hasLocks()) return variant;
    const fits = (v) =>
      v.option1 === variant.option1 && Object.entries(this.locks).every(([i, value]) => v[`option${i}`] === value);
    return this.variants.find((v) => fits(v) && v.available) || this.variants.find(fits) || variant;
  }

  /**
   * The locked values as the voucher's price map keys them (_kc-upsell-modal-sticky-footer: option2|option3),
   * or null unless the sofa decides every further option of this product.
   */
  lockKey() {
    const further = [2, 3].filter((i) => this.variants.some((v) => v[`option${i}`]));
    if (!further.length || !further.every((i) => this.locks[i] !== undefined)) return null;
    return further.map((i) => this.locks[i]).join("|");
  }

  applyLockUi() {
    if (!this.form) return;
    const names = (this.optionNames || "").split(",");
    const label = this.dataset.kcSofaLabel || "";
    [2, 3].forEach((i) => {
      const name = names[i - 1];
      if (!name) return;
      const value = this.locks[i];
      const inputs = [...this.form.querySelectorAll("input[name], select[name]")].filter((el) => el.name === name);
      const container = inputs.map((el) => el.closest(".variant-list-container")).find(Boolean);
      if (value === undefined) {
        if (container && container.classList.contains("kc-option-locked")) {
          container.classList.remove("kc-option-locked");
          container.querySelectorAll(".variant-list").forEach((el) => (el.style.display = ""));
          container.querySelectorAll(".kc-option-locked__note").forEach((el) => el.remove());
        }
        return;
      }
      inputs.forEach((el) => {
        if (el.type === "radio") el.checked = el.value === value;
        else el.value = value;
      });
      if (!container) return;
      container.classList.add("kc-option-locked");
      container.querySelectorAll(".variant-list").forEach((el) => (el.style.display = "none"));
      const title = container.querySelector(`.variant-list-title-value[data-option-name="${name}"]`);
      if (title) {
        title.textContent = value;
        let note = container.querySelector(".kc-option-locked__note");
        if (!note && label) {
          note = document.createElement("span");
          note.className = "kc-option-locked__note";
          note.style.opacity = "0.6";
          title.after(note);
        }
        if (note) note.textContent = ` (${label})`;
      }
    });
  }

  /**
   * The sofa's variant changed on the product page: new locks, and the cover already in the set (its slot
   * input) moves to the same fabric in the sofa's new values, as if the customer had saved it. A voucher in
   * the slot is not one of this product's variants and is left to kc-cover-voucher.js.
   */
  followSofa(values) {
    this.sofaValues = values;
    this.locks = this.computeLocks();
    this.applyLockUi();
    if (!this.hasLocks()) return;

    const slot = document.querySelector(`input[data-input-id="${this.referenceCheckboxId}"]`);
    const current = slot && this.variants.find((v) => v.id === parseInt(slot.value, 10));
    if (current) {
      const target = this.lockedTwin(current);
      if (target && target.id !== current.id) this.commitVariant(target);
    }
    if (this.selectedVariant) {
      this.selectedVariant = this.lockedTwin(this.selectedVariant);
      this.preselectOptionsSequentially(this.selectedVariant);
    }
  }

  commitVariant(variant) {
    document.querySelectorAll(`input[data-input-id="${this.referenceCheckboxId}"]`).forEach((input) => {
      input.value = variant.id;
    });
    document.querySelectorAll(`input[data-upsell-product-key="${this.referenceCheckboxId}"]`).forEach((checkbox) => {
      checkbox.value = variant.id;
      const container = checkbox.closest("[data-upsell-matcher]");
      const priceList = container && container.querySelector(".price-list");
      if (priceList) this.replacePriceList(priceList, variant);
    });
    this.notifyVariantSelected(variant);
  }

  async handleSaveButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();

    // /cart: hand the choice to <main-cart>. "cart-edit" swaps a line (add new, then remove old); "cart-offer" adds
    // the picked variant of an "add to your order" product. Both re-render the page in place.
    const source = this.modalElement && this.modalElement.getAttribute("data-upsell-source");
    if (source === "cart-edit" || source === "cart-offer") {
      if (this.selectedVariant) {
        document.dispatchEvent(
          new CustomEvent(source === "cart-edit" ? "cart:edit-variant" : "cart:add-variant", {
            bubbles: true,
            detail: { variantId: this.selectedVariant.id, lineKey: this.modalElement.getAttribute("data-edit-line-key") },
          })
        );
      }
      this.closeModal();
      return;
    }

    if (this.selectedVariant) {
      const openedFromCart =
        this.modalElement &&
        this.modalElement.getAttribute("data-upsell-source") === "cart";

      const referenceCheckboxes = document.querySelectorAll(
        `input[data-upsell-product-key="${this.referenceCheckboxId}"]`
      );

      referenceCheckboxes.forEach((referenceCheckbox) => {
        const referenceCheckboxContainer = referenceCheckbox.closest("[data-upsell-matcher]");

        if (!referenceCheckboxContainer) return;

        const imageElement =
          referenceCheckboxContainer.querySelector(".upsell-image");
        const selectedImageSrc =
          (this.selectedVariant.featured_image &&
            this.selectedVariant.featured_image.src) ||
          (this.selectedVariant.featured_media &&
            this.selectedVariant.featured_media.preview_image &&
            this.selectedVariant.featured_media.preview_image.src) ||
          null;
        if (imageElement && selectedImageSrc) {
          const media = `
            <img src="${selectedImageSrc}" alt="${this.selectedVariant.title}">
          `;
          this.replaceMedia(
            imageElement,
            media,
            selectedImageSrc
          );
        }

        const priceListElement =
          referenceCheckboxContainer.querySelector(".price-list");
        if (priceListElement) {
          this.replacePriceList(priceListElement, this.selectedVariant);
        }

        // Update variant options in data attribute for bundle summary
        const variantOptions = [
          this.selectedVariant.option1,
          this.selectedVariant.option2,
          this.selectedVariant.option3,
        ]
          .filter((opt) => opt && opt !== "Default Title")
          .join(" / ");

        if (variantOptions) {
          referenceCheckboxContainer.dataset.currentVariantOptions =
            variantOptions;
        }

        // Auto-check the checkbox when variant is selected
        if (!referenceCheckbox.checked) {
          referenceCheckbox.checked = true;
          referenceCheckbox.dispatchEvent(
            new Event("change", { bubbles: true })
          );
        }
      });

      const upsellInputs = document.querySelectorAll(
        `input[data-input-id="${this.referenceCheckboxId}"]`
      );
      upsellInputs.forEach((upsellInput) => {
        upsellInput.value = this.selectedVariant.id;
      });

      this.notifyVariantSelected(this.selectedVariant);

      if (openedFromCart) {
        if (this.saveButton) this.saveButton.setAttribute("aria-busy", "true");
        await this.addVariantToCart(this.selectedVariant.id);
        if (this.saveButton) this.saveButton.removeAttribute("aria-busy");
      }
    }

    this.closeModal();
  }

  async addVariantToCart(variantId) {
    if (!variantId) return;

    try {
      const root = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.description || errorData.message || null;
        document.dispatchEvent(
          new CustomEvent("cart:error", {
            bubbles: true,
            detail: { message },
          })
        );
        return;
      }

      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      document.dispatchEvent(new CustomEvent("cart:change", { bubbles: true }));
    } catch (error) {
      // Silent fail: do not block modal close.
    }
  }

  replaceMedia(container, media, src) {
    if (!media) return;

    const loadingOverlay = document.createElement("div");
    loadingOverlay.className = "loading-overlay";
    loadingOverlay.innerHTML = `
      <div class="loading-spinner"></div>
    `;
    container.appendChild(loadingOverlay);

    const newImage = new Image();
    newImage.onload = () => {
      container.innerHTML = media;
      loadingOverlay.remove();
    };
    newImage.onerror = () => {
      loadingOverlay.remove();
    };
    newImage.src = src;
  }

  formatMoney(priceInCents) {
    if (!this.moneyFormat || priceInCents === undefined) return "";

    const amount = priceInCents / 100;
    const amountWithDot = amount.toFixed(2);
    const amountWithComma = amountWithDot.replace(".", ",");
    const amountNoDecimals = Math.floor(amount).toString();

    let formattedPrice = this.moneyFormat
      .replace("{{amount_with_comma_separator}}", amountWithComma)
      .replace("{{amount_no_decimals_with_comma_separator}}", amountNoDecimals)
      .replace(
        "{{amount_with_space_separator}}",
        amountWithDot.replace(/\B(?=(\d{3})+(?!\d))/g, " ")
      )
      .replace("{{amount_no_decimals}}", amountNoDecimals)
      .replace("{{amount}}", amountWithDot);

    return formattedPrice;
  }

  replacePriceList(priceList, variant) {
    // A cover in Black Weeks: the price the checkout charges, the list price struck through (as kc-bf-offer.js
    // paints the set row), so the modal and its Gutschein quote the same cover (Ivan 04/11).
    const off = this.coverOff || 0;
    const price = off > 0 ? Math.round((variant.price * (100 - off)) / 100) : variant.price;
    const compare = off > 0 ? Math.max(variant.compare_at_price || 0, variant.price) : variant.compare_at_price;
    const salePrice = this.formatMoney(price);
    const compareAtPrice = this.formatMoney(compare);

    priceList.innerHTML = `
        <sale-price class="text-on-sale">${salePrice}</sale-price>
      `;

    if (compare > price) {
      priceList.innerHTML += `
          <compare-at-price class="line-through text-subdued">${compareAtPrice}</compare-at-price>
        `;
    }
  }

  closeModal() {
    if (!this.modalElement) return;
    this.modalElement.removeAttribute("data-upsell-source");
    const closeButton = this.modalElement.querySelector(
      'button[is="close-button"]'
    );
    if (closeButton) closeButton.click();
  }
}

if (!customElements.get("upsell-variant-selector")) {
  customElements.define("upsell-variant-selector", UpsellVariantSelector);

  // The product page's sofa changed (product-detail-variation.js / theme.js): every cover that follows it
  // takes the new values. Pickers inside a modal dispatch nothing of the kind, but are skipped all the same.
  document.addEventListener("variant:change", (event) => {
    if (event.target && event.target.closest && event.target.closest("upsell-variant-selector, x-drawer")) return;
    const variant = event.detail && event.detail.variant;
    if (!variant) return;
    const values = [variant.option1, variant.option2, variant.option3].filter((value) => value != null && value !== "");
    if (!values.length) return;
    document.querySelectorAll("upsell-variant-selector[data-kc-sofa]").forEach((selector) => {
      if (typeof selector.followSofa === "function") selector.followSofa(values);
    });
  });
}
